import React, { useEffect, useState } from 'react';
import Echo from 'laravel-echo';
import { useDispatch } from 'react-redux';
import { Route, Switch, useRouteMatch, useHistory } from 'react-router-dom';

import { apiHost } from '../../config';
import getEchoConfig from '../../lib/getEchoConfig';
import { post } from '../../lib/requests';
import getAllMunicipalities from '../../lib/apiClient/municipalities/getAll';
import getAllCities from '../../lib/apiClient/cities/getAll';
import getAllSchools from '../../lib/apiClient/schools/getAll';
import getAllSchoolTypesAsAdmin from '../../lib/apiClient/schoolTypesAdmin/getAll';
import getAllTeachers from '../../lib/apiClient/teachers/getAll';
import getAllStudents from '../../lib/apiClient/studentsAdmin/getAll';
import getAllStudentsResults from '../../lib/apiClient/studentsResultsAdmin/getAll';
import getAllGradesAsAdmin from '../../lib/apiClient/gradesAdmin/getAll';
import handleUnauthorized from '../../lib/handleUnauthorized';

import subscribeToMunicipalitiesChannel from '../../lib/socketClient/municipalities';
import subscribeToCitiesChannel from '../../lib/socketClient/cities';
import subscribeToSchoolsChannel from '../../lib/socketClient/schools';
import subscribeToTeachersChannel from '../../lib/socketClient/teachers';

import Navbar from '../../components/common/Navbar/Navbar';
import SidePanel from '../../components/common/SidePanel/SidePanel';
import LoginForm from '../../components/common/LoginForm/LoginForm';

import MunicipalitiesPage from './municipalities/index';
import CitiesPage from './cities/index';
import SchoolsPage from './schools/index';
import TeachersPage from './teachers/index';
import StudentsPage from './students/index';

import { setAllCities } from '../../redux/reducers/CitiesReducer';
import { setAllSchools } from '../../redux/reducers/SchoolsReducer';
import { setAllTeachers } from '../../redux/reducers/TeachersReducer';
import { setAllMunicipalities } from '../../redux/reducers/MunicipalitiesReducer';
import { setAllStudents } from '../../redux/reducers/StudentsReducer';
import { setAllGrades } from '../../redux/reducers/GradesReducer';
import { setAllStudentsResults } from '../../redux/reducers/StudentsResultsReducer';
import { setAllSchoolTypes } from '../../redux/reducers/SchoolTypesReducer';

import styles from './Layout.module.scss';
import ImportsPage from './imports';

const AdminPage = () => {
  const match = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();

  const [authorized, setAuthorized] = useState<boolean | undefined>(undefined);

  const sidePanelTabs = [
    { label: 'Муниципалитеты', href: '/admin/municipalities' },
    { label: 'Города', href: '/admin/cities' },
    { label: 'Школы', href: '/admin/schools' },
    { label: 'Учителя', href: '/admin/teachers' },
    { label: 'Ученики', href: '/admin/students' },
    { label: 'Импорт / экспорт учителей', href: '/admin/imports' },
  ];

  const unauthorizedCallback = () => setAuthorized(false);

  const loadData = () => {
    getAllMunicipalities()
      .then((response) => {
        dispatch(setAllMunicipalities(response.rows));
      })
      .catch(handleUnauthorized(unauthorizedCallback));

    getAllCities()
      .then((response) => {
        dispatch(setAllCities(response.rows));
      })
      .catch(handleUnauthorized(unauthorizedCallback));

    getAllSchoolTypesAsAdmin()
      .then((response) => {
        dispatch(setAllSchoolTypes(response.rows));
      })
      .catch(handleUnauthorized(unauthorizedCallback));

    getAllSchools()
      .then((response) => {
        dispatch(setAllSchools(response.rows));
      })
      .catch(handleUnauthorized(unauthorizedCallback));

    getAllTeachers()
      .then((response) => {
        dispatch(setAllTeachers(response.rows));
      })
      .catch(handleUnauthorized(unauthorizedCallback));

    getAllStudents()
      .then((response) => {
        dispatch(setAllStudents(response.rows));
      })
      .catch(handleUnauthorized(unauthorizedCallback));

    getAllGradesAsAdmin()
      .then((response) => {
        dispatch(setAllGrades(response.rows));
      })
      .catch(handleUnauthorized(unauthorizedCallback));

    getAllStudentsResults()
      .then((response) => {
        dispatch(setAllStudentsResults(response.rows));
      })
      .catch(handleUnauthorized(unauthorizedCallback));
  };

  const checkIfUserIsAdmin = () => {
    post(`${apiHost}/api/middleware/check_if_user_is_admin`, {})
      .then(() => {
        setAuthorized(true);
        loadData();

        const echo = new Echo(getEchoConfig());
        echo.connect();

        subscribeToMunicipalitiesChannel(echo, dispatch);
        subscribeToCitiesChannel(echo, dispatch);
        subscribeToSchoolsChannel(echo, dispatch);
        subscribeToTeachersChannel(echo, dispatch);
      })
      .catch(() => history.push('/login'));
  };

  useEffect(checkIfUserIsAdmin, []);

  if (authorized === undefined) return null;

  return (
    <>
      <Navbar />

      <SidePanel tabs={sidePanelTabs} />

      <div className={styles.admin_page_layout__main_container}>
        <Switch>
          <Route path={`${match.url}/municipalities`} exact>
            <MunicipalitiesPage unauthorizedCallback={unauthorizedCallback} />
          </Route>

          <Route path={`${match.url}/cities`} exact>
            <CitiesPage unauthorizedCallback={unauthorizedCallback} />
          </Route>

          <Route path={`${match.url}/schools`} exact>
            <SchoolsPage unauthorizedCallback={unauthorizedCallback} />
          </Route>

          <Route path={`${match.url}/teachers`} exact>
            <TeachersPage unauthorizedCallback={unauthorizedCallback} />
          </Route>

          <Route path={`${match.url}/students`}>
            <StudentsPage />
          </Route>

          <Route path={`${match.url}/imports`} exact>
            <ImportsPage unauthorizedCallback={unauthorizedCallback} />
          </Route>
        </Switch>
      </div>

      {!authorized && (
        <LoginForm
          onSuccess={({ role }) => {
            if (role !== 1) {
              throw new Error('Войдите под учетной записью администратора');
            }

            setAuthorized(true);
            loadData();
          }}
        />
      )}
    </>
  );
};
export default AdminPage;
